import type { Components, Theme } from '@mui/material';

import { colors, palette } from '../colors';

export const getMuiButton = (): Components<Theme>['MuiButton'] => ({
    defaultProps: {
        disableRipple: true,
        disableElevation: true,
        variant: 'contained',
        color: 'primary',
    },
    styleOverrides: {
        root: {
            textTransform: 'none',
            fontWeight: 600,
            variants: [
                {
                    props: { variant: 'outlined' },
                    style: {
                        border: `1px solid ${palette.border?.main}`,
                    },
                },
                {
                    props: { variant: 'outlined', color: 'primary' },
                    style: {
                        color: colors.neutral[900],
                        backgroundColor: colors.neutral[0],
                        '&:hover': {
                            backgroundColor: colors.green[100],
                            borderColor: colors.green[600],
                            color: colors.green[700],
                        },
                        '&:focus': {
                            backgroundColor: colors.green[200],
                            borderColor: colors.green[700],
                            color: colors.green[700],
                        },
                    },
                },
                {
                    props: { variant: 'outlined', color: 'secondary' },
                    style: {
                        backgroundColor: colors.neutral[0],
                        '&:hover': {
                            backgroundColor: colors.neutral[100],
                        },
                        '&:focus': {
                            backgroundColor: colors.neutral[200],
                        },
                    },
                },
                {
                    props: { color: 'contrasted', variant: 'outlined' },
                    style: {
                        border: `1px solid ${colors.neutral[0]}`,
                        backgroundColor: 'transparent',
                        color: colors.neutral[0],
                        '& .MuiLoadingButton-loadingIndicator': {
                            color: palette.text?.disabled,
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:focus': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:disabled': {
                            color: palette.text?.disabled,
                            borderColor: palette.text?.disabled,
                        },
                    },
                },
                {
                    props: { color: 'contrasted', variant: 'contained' },
                    style: {
                        backgroundColor: colors.neutral[0],
                        '&:hover': {
                            backgroundColor: colors.neutral[100],
                        },
                        '&:focus': {
                            backgroundColor: colors.neutral[200],
                        },
                        '&:disabled': {
                            color: colors.neutral[400],
                            backgroundColor: colors.neutral[200],
                            borderColor: palette.text?.disabled,
                        },
                    },
                },
                {
                    props: { color: 'ai', variant: 'contained' },
                    style: {
                        backgroundColor: colors.purple[500],
                        color: palette.text?.contrast,
                        '&:hover': {
                            backgroundColor: colors.purple[700],
                        },
                        '&:focus': {
                            backgroundColor: colors.purple[800],
                        },
                        '&:disabled': {
                            backgroundColor: colors.purple[200],
                            color: palette.text?.contrast,
                        },
                    },
                },
            ],
        },
    },
});

declare module '@mui/material' {
    interface ButtonPropsColorOverrides {
        contrasted: true;
        ai: true;
    }
}
